/**
 * TTS narration sync helpers.
 *
 * OpenAI TTS doesn't expose word-level timestamps, so we *estimate* which
 * sentence is being spoken by mapping `audio.currentTime / audio.duration`
 * onto cumulative character offsets in the original text. Good enough for a
 * karaoke-style "Now reading" caption that lets the student follow along.
 */

// Strip markdown/LaTeX/code fences so the spoken text matches what TTS sees.
// (Mirrors the cleaning the backend does for OpenAI TTS.)
export function cleanForNarration(raw) {
  if (!raw) return "";
  let s = String(raw);
  // Code blocks
  s = s.replace(/```[\s\S]*?```/g, " ");
  s = s.replace(/`[^`]+`/g, " ");
  // Inline SVG diagrams
  s = s.replace(/<svg[\s\S]*?<\/svg>/gi, " ");
  // LaTeX
  s = s.replace(/\$\$[\s\S]*?\$\$/g, " ");
  s = s.replace(/\$[^$\n]+\$/g, " ");
  // Markdown emphasis / headings / list bullets
  s = s.replace(/[#*_>`~]+/g, " ");
  // Links: keep label, drop url
  s = s.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  // Collapse whitespace
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

// Split text into sentences. Preserves trailing punctuation so the spoken
// boundary visually matches the text.
export function splitSentences(text) {
  const cleaned = cleanForNarration(text);
  if (!cleaned) return [];
  // Split on sentence-ending punctuation followed by space + capital/digit,
  // and on newlines. Keep the delimiter.
  const raw = cleaned.split(/(?<=[.!?])\s+(?=[A-Z0-9"'(\u00C0-\u017F])|\n+/);
  return raw.map((s) => s.trim()).filter(Boolean);
}

// Build cumulative char offsets so we can map elapsed seconds → sentence idx.
export function buildOffsets(sentences) {
  const total = sentences.reduce((sum, s) => sum + s.length, 0) || 1;
  let acc = 0;
  const offsets = sentences.map((s) => {
    const start = acc;
    acc += s.length;
    return start / total; // 0..1
  });
  return { offsets, total };
}

// Given normalized progress (0..1), return the index of the sentence playing.
export function indexForProgress(offsets, progress) {
  if (!offsets.length) return -1;
  // Binary search would be overkill; sentences are small in count (<200).
  for (let i = offsets.length - 1; i >= 0; i -= 1) {
    if (progress >= offsets[i]) return i;
  }
  return 0;
}
